import {
  BarcodeExportType,
  ECustomFieldType,
  EDateType,
  EEncodingType,
  EGraphFunction,
  EGraphOption,
  EGraphType,
  ELocationItemFunction,
  EPathType,
  ETimeTableDirection,
  EUserType,
} from '../enums/index'
import {
  IActivationData,
  IBarcodeSearchForm,
  IBarcodeSearchFormItem,
  IDimension,
  IListStringItem,
  ILocationData,
  IProductData,
  ISearchConditionBlock,
  ITrackingData,
  IUser,
} from './index'
import { IAuditData, IBarcodeAuditData } from './model/audit'
import { IReferenceCustomFieldData } from './model/fieldData'
import {
  IAddress,
  IBarcodeDefinitionType,
  ICustomFieldFormData,
  IProjectCustomField,
  IRoutePath,
} from './model/fieldDefinition'

import {
  IActivationDataVisibility,
  IBaseProject,
  ICreateUserGroupForm,
  IPermission,
  IProject,
  ITrackingPermissions,
  ITrackpoint,
  ITrackPointTracingVisibilities,
} from './model/project'

export interface ILoginPayload {
  email: string
  password: string
  activate_code?: string
  visitorId: string
}

export interface IGraphRequest extends IChartOption {
  graphId: string
}

export interface IChartOption {
  id?: string
  name: string
  projectCode: string
  version: string
  barcodeType: string
  type: EGraphType
  subType: EGraphOption
  groupBys: IGroupByFormItem[]
  functions: IChartFunction[]
  sorts: ISortOption[]
  logicalOperator: string
  filterConditions: IBarcodeSearchFormItem[]
}

export interface IGroupByFormItem extends IBarcodeSearchFormItem {
  customFieldType?: string
  dateType?: EDateType
}

export interface IChartFunction {
  key: string
  label?: string
  type: EGraphFunction
}

export interface ISortOption {
  key: number
  type: string
}

export interface IGetTimetablePayload {
  date: string
}

export interface IGetTimetableSchedulePayload {
  timetableId: string
}

export interface IAddressResponse {
  addressHistory: IAddress[]
  addressBook: IAddress[]
}

export interface INewPasswordPayload {
  token: string
  email: string
  password: string
  visitorId?: string
}

export interface IPoint {
  time: string
  state: string
}

export interface IRoute {
  state: string
}

export interface IChartRecord {
  projectCode: string
  projectName: string
  date?: string
  barcodeType?: string
  barcodeTypeName?: string
  currentTrackPointKey?: string
  currentTrackPointName?: string
  count: number
}

export interface IDialogMessage {
  isVisible?: boolean
  title?: string
  content?: string
  buttonContent?: string
}

/** ============================= AUDIT DATA ============================= */
export interface ICreatedAuditData {
  createdUserId: string
  createdDateTime: string
}

export interface IBarcodeImportAuditData extends IAuditData {
  processedDateTime: string
}
/** ============================= END AUDIT DATA ============================= */

/** ============================= TIMETABLE ============================= */
export interface IStopPoint {
  from?: IPoint | undefined
  to?: IPoint | undefined
  routeState?: string
  prevRoute?: string | undefined
  hubKey?: string
}

export interface ITimeTableHeader {
  stopPointLabels: string[]
}

export interface ITimeTableRecord {
  index: string
  [ETimeTableDirection.INBOUND]: IRound
  [ETimeTableDirection.OUTBOUND]: IRound
}

export interface IRound {
  label: string
  stopPointData: IStopPoint[]
}

export interface IOriginTimeTable {
  projectId: string
  date: string
  tableView: ITableView
  timetableSetting: ITimetableSetting
  id: string
  autit: IAuditData
  isActive: boolean
}

export interface ITableView {
  [index: string]: IOriginTimeTableView
}

export interface IRouteName {
  [ETimeTableDirection.INBOUND]: string
  [ETimeTableDirection.OUTBOUND]: string
}

export interface ITimetableSetting {
  enterpriseId: string
  projectId: string
  hubs: {
    [index: string]: {
      shortName: string
    }
  }
  inboundRowLabel: string
  outboundRowLabel: string
  id: string
  audit: IAuditData
  isActive: boolean
}

export interface ITimeTableSchedulePath {
  direction: ETimeTableDirection
  scheduledTime: string
  pathType: EPathType
  hubName: string
  containerLocationCode: number
  isAcked: boolean
  hubKey: number
}

export interface ITimeTableSchedule {
  timetableCode: string
  projectCode: string
  operatingCompanyName: string
  lineCode: string
  vehicleId: string
  routeNumber: number
  barcodeId: string
  pathes: ITimeTableSchedulePath[]
}

export interface IOriginTimeTableView {
  [ETimeTableDirection.INBOUND]: {
    [index: string]: IHubData
  }
  [ETimeTableDirection.OUTBOUND]: {
    [index: string]: IHubData
  }
}

export interface IHubData {
  arrivalTimeSchedule?: string
  arrivalStatus?: string
  departureTimeSchedule?: string
  departureStatus?: string
}
export interface ITimetableStopPointInfo {
  CurrentState: string
  TimetableId: string
  LineCode: string
  Direction: ETimeTableDirection
  HubKey: string
  PathType: EPathType
  Ack?: true
  TimeValue?: string
}

export interface ITimetableUpdateNode {
  TimetableId?: string
  LineNumber?: string
  TrackPointOriginKey?: string
  TrackPointDestinationKey?: string
  NodeType: string
  ScheduledTime?: string
  utcOffset?: number
  Ack?: boolean
}

export interface ITimetableUpdateStatusData {
  TimetableId: string
  LineCode: string
  Direction: string
  HubKey: string
  PathType: EPathType
  Ack: true
}

export interface ITimetableUpdateTimeData {
  TimetableId: string
  LineCode: string
  Direction: ETimeTableDirection
  HubKey: string
  PathType: EPathType
  ScheduledTime: string
}

export interface ISinglePointEvent {
  state: string
  time: string
  isArrival: boolean
  prevRoute?: string
  class: string[]
}

export interface IDouplePointEvent {
  from: ISinglePointEvent
  to: ISinglePointEvent
  routeState?: string
  prevRoute?: string
}

export interface ITimeTableContent {
  routeName: {
    [ETimeTableDirection.INBOUND]: string
    [ETimeTableDirection.OUTBOUND]: string
  }
  tableView: ITableView
}

/** ============================= PROJECT SETTINGS =========================== */
export interface IProjectsList {
  projects?: IProject[]
  projectDrafts?: IProject[]
}

export type IProjectDraft = IBaseProject & {
  userGroups?: IProjectUserGroup[]
  locations?: IProjectLocationPayload[]
}

export type IProjectForm = IProject & {
  permissionGroup?: ICreateUserGroupForm[]
}

export interface IProjectDetailMainInfo {
  id?: string
  showBarcodeSearchPage?: boolean
  showTrackingCountPage?: boolean
  allowUnmatchedTracking?: boolean
  allowForcePairing?: boolean
  isAutoRecycleEndTrackingPoint?: boolean
  isRecycleChildren?: boolean
  targetProjectId?: string
  isDraft?: boolean
  draftId?: string
  name?: string
  code?: string
  version?: number
  trackingPointCount?: number
  barcodeTypeCount?: number
  lastUpdatedBy?: string
  lastUpdatedDateTime?: string
  googleAnalyticsMeasurementId?: string
}
export interface IProjectDetail {
  mainInfo: IProjectDetailMainInfo
  trackPoints?: Record<string, ITrackpoint>
  barcodeTypes?: Record<string, IBarcodeDefinitionType>
  permissionGroup?: ICreateUserGroupForm[]
}

export interface ICustomFieldVisibilities {
  [ECustomFieldType.DATE]: boolean
  [ECustomFieldType.ESIGN]: boolean
  [ECustomFieldType.FILES]: boolean
  [ECustomFieldType.SINGLE_SELECT]: boolean
  [ECustomFieldType.MULTI_SELECT]: boolean
  [ECustomFieldType.NUMBER]: boolean
  [ECustomFieldType.TEXT]: boolean
  [ECustomFieldType.PHONE_NUMBER]: boolean
  [ECustomFieldType.EMAIL]: boolean
}

// =================== END CUSTOM FIELD TYPE COMPONENTS ===========

export type TArrProjectCustomField = IProjectCustomField & {
  bcTypeKey: string
  customFieldKey: string
}

export interface ICreateUserGroup {
  newGroupId?: string
  userIds: string[]
  name: string
  id: string
  userGroupId?: string
  isNewGroup?: boolean
  isActive?: boolean
  projectPermissions: ITrackingPermissions
}

export interface ICarrierPermission {
  id: string
  name: string
  isCheck: string
  projectIds: string[]
}

export type IFieldSettingValidation = IProjectCustomField & {
  labelError?: string
}

export type ICustomFieldCustomValidation = ICustomFieldFormData & {
  fromName: string
  toName: string
}

/** ================================ BARCODE =========================== */

export interface IBarcodeCustomField {
  fieldType: ECustomFieldType
  [ECustomFieldType.DATE]?: string
  [ECustomFieldType.DATE_TIME_DATA]?: string
  [ECustomFieldType.ESIGN]?: string
  [ECustomFieldType.FILES]?: IFileItem[]
  [ECustomFieldType.SINGLE_SELECT]?: ISingleSelect
  [ECustomFieldType.MULTI_SELECT]?: ISingleSelect[]
  [ECustomFieldType.NUMBER]?: number
  [ECustomFieldType.TEXT]?: string
  [ECustomFieldType.PHONE_NUMBER]?: IPhoneObject
  [ECustomFieldType.EMAIL]?: string
  [ECustomFieldType.REFERENCE]?: IReferenceCustomFieldData
  [ECustomFieldType.CALCULATION]: string
  [ECustomFieldType.ADJUSTMENT]?: number
  [ECustomFieldType.LIST_STRING]?: IListStringItem[]
  currentValue?: number
}

export interface ISingleSelect {
  key: string
  value: string
}

export interface IProjectLocation extends ILocationProjectPermission {
  locationId: string
  locationName: string
  projectId: string
}

export interface IProjectLocationPayload extends ILocationProjectPermission {
  locationId?: string
  name: string
}

/** ================================ END BARCODE =========================== */

export interface ICustomFieldFileView {
  label: string
  text?: string
  files: Array<IFileItem>
}

export interface IFileItem {
  path: string
  url: string
  name: string
}

export interface IIndexPeriodData {
  startKey: string
}

export interface IBaseUserInfo {
  id?: string
  firstName: string
  lastName: string
}

export interface IBaseContact {
  email: string
  phone: IPhoneObject
}

export interface IStaff {
  roleType?: string
  userType?: EUserType
  clientIds?: string[]
  remarks?: string
  accessCode?: string
  organizationId?: string
  firstName?: string
  lastName?: string
  phone: IPhoneObject
  userGroupIds?: string[]
  groups?: ICreateUserGroup[]
  fullName?: string
  id?: string
  audit?: IAuditData
  isCheck?: boolean
  isActive?: boolean
}

export interface IPhoneObject {
  number: string
  countryCode: string
}
export interface IClientList {
  client: {
    id?: string
    enterpriseIds?: Array<string>
    name: string
    address: IAddress
    audit: ICreatedAuditData
    isActive: boolean
  }
  clientUser: IBaseUserInfo &
    IBaseContact & {
      visibleProjectCodes?: string[]
      roleType: string
      userType: EUserType
      accessCode: string
      organizationId: string
      userGroupIds: string
      fullName: string
      audit: ICreatedAuditData
      isActive: boolean
      carrierIds?: string[]
      setPresetSearch?: boolean
      condition?: IBarcodeSearchForm
    }
  clientVisibilities?: IClientDetailVisibilities[]
}

/** =================== LOCATION =================== */

export interface ILocation {
  id: string
  enterpriseId: string
  name: string
  audit?: IAuditData
  locationProjectPermissions: Record<string, ILocationProjectPermission>
  isActive: boolean
  totalProject?: number
  totalBarcode?: number
}

export interface ILocationPayload {
  locationId: string
  isActive: boolean
  enterpriseId: string
  name: string
  audit?: IAuditData
  locationProjectPermissions: Record<string, ILocationProjectPermission>
}

export interface IPaginationPayload {
  skip?: number
  count?: number
}

export interface ILocationPermissionPayload {
  locationId: string
  projectCode?: string
  skip?: number
  count?: number
}

export interface ILocationProjectPermission {
  availableBarcodeTypes: string[]
  trackingPermissions: Record<string, ILocationTrackingPermission>
  locationItems: ILocationSettingItem[]
}
export interface ILocationValueItem {
  value: number
  label: string
  unit: string
}
export interface ILocationSettingItem {
  barcodeTypeCode: string
  customFieldType: string
  field: string
  function: ELocationItemFunction
  label: string
  unit: string
}

export interface ILocationTrackingPermission {
  canInStock: boolean
  canOutStock: boolean
}

// ===================================================

export interface IClient {
  isEdit: boolean
  clientName: string
  address: IAddress
  visibleProjectCodes?: string[]
  clientUser: IBaseUserInfo &
    IBaseContact & {
      password?: string
      roleType?: string
      visibleProjectCodes?: string[]
      carrierIds?: string[]
      setPresetSearch?: boolean
      condition?: IBarcodeSearchForm
    }
  isActive?: boolean
  clientVisibilities?: IClientDetailVisibilities[]
}
export interface IClientDetailVisibilities {
  projectId: string
  barcodeTypeCode: string
  activationDataVisibility: IActivationDataVisibility
  trackPointTracingVisibilities: Record<string, ITrackPointTracingVisibilities>
  projectCode: string
  projectVersion: number
}

export interface IClientUser extends IBaseUserInfo, IBaseContact {
  userId: string
  isActive: boolean
  isEditPassword: boolean
  password: string
  roleType: string
}

export interface IEnterpriseList extends IBaseUserInfo {
  roleType?: string
  userType?: EUserType
  organizationId: string
  email: string
  fullName: string
  audit: ICreatedAuditData
  isActive: boolean
}

export interface IEnterprise extends IBaseUserInfo {
  isEdit: boolean
  email: string
  password?: string
  roleType?: string
  encodingType: EEncodingType
  isActive?: boolean
  entitledPortalUIViews?: Array<string>
  visibleProjectCodes: string[]
}

export interface IEnterpriseUser {
  userId: string
  isActive: boolean
  email: string
  isEditPassword: boolean
  password: string
  firstName: string
  lastName: string
  phoneNumber: string
  roleType: EUserType
  encodingType: EEncodingType
  visibleProjectCodes: string[]
}

export interface IGlobalGetters {
  isAuth?: boolean
  isEnableTimetable?: boolean
  isLoading?: boolean
  userType?: EUserType
  alreadyGetProfileOnInit?: boolean
  userId?: string
  getProjectsList?: []
  getProjectsTrackingPoints?: []
  getProjectReadonly?: boolean
  getProducts?: boolean
}

export interface IBarcodeSearchConditionItem {
  key: string
  value?: string
  minValue?: string | number
  maxValue?: string | number
  valueType: string
  valueList?: string[]
}

export interface IBarcodeSearchAutoCompleteRequest {
  condition: {
    projectCode: string
    version: number
    projectId: string
    autoComplete: IAutoComplete
  }
}

export interface IBarcodeSearch {
  condition: {
    projectCode: string
    searchConditionBlocks: ISearchConditionBlock[]
    logicalOperator?: string
    count: number
    skip: number
    sortKey: string
    sortOrder: number
  }
}

export interface IBarcodeTypeDescription {
  name: string
  icon: string
  invertIconColor: boolean
  backgroundColor: string
  foregroundColor: string
}

export interface IBarcodeList {
  id: string
  barcodeId?: string
  projectId: string
  barcodeType: string
  barcodeTypeDefinition?: IBarcodeTypeDescription
  isActive: boolean
  isActivated: boolean
  currentTrackPointKey: string
  currentTrackPointName?: string
  activationData: IActivationData
  activatedDateTime: string
  isDeactivated: boolean
  deactivatedDateTime: string
  audit: IBarcodeAuditData
  qrUrl?: string
}

export interface IAutoComplete {
  key: string
  value: string
}

export interface IBarcodeDetail {
  projectId: string
  rootId: string
  barcodeType: string
  startTrackPointKeyOverride?: string
  childrenCount: number
  activationData: IActivationData
  trackingData: Array<ITrackingData>
  deletedTrackingData?: Array<ITrackingData>
  trackingLocations?: ILocationData[]
  isActivated: boolean
  activatedDateTime?: string
  isDeactivated: boolean
  currentTrackPointKey: number | string
  hasParent: boolean
  parentId?: string
  hasValidTrackingNumber: boolean
  hasValidExternalId?: boolean
  isArchivedRecycleBarcode?: boolean
  isReadyToAddTrackData?: boolean
  isLedgerEnabled?: boolean
  isSensorEnabled?: boolean
  productDatas?: IProductData[]
  pairHistory?: Record<
    string,
    Array<{
      audit: {
        createdUserId: string
        createdDateTime: string
      }
      trackPointKey: string
      type: 'pair' | 'unpair'
    }>
  >
  recycleCount?: number
  id: string
  isActive: true
  audit: IBarcodeAuditData
}

export interface IBarcodeResult {
  count: number
  results: Array<IBarcodeList>
}

export interface ISelectData {
  id: string
  name: string
}

export interface ITableSortData {
  column: Record<string, unknown>
  prop: string
  order: string
}

export interface IBarcodeImportSearch {
  projectCode: string
  version: number
  sortKey?: string
  sortOrder?: number
}
export interface IBarcodeImportSearchPayload {
  projectCode: string
  version: number
  skip?: number
  count?: number
  t: Function
  te: Function
}

export interface IBarcodeImportRequestPayload {
  id: string
  skip: number
  count: number
  type: 'success' | 'error'
}
export interface ICommonSearch {
  text: string
  sortKey?: string
  sortOrder?: number
}

export interface IRequestUser {
  roleType: string
  userType: EUserType
  organizationId: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  id: string
  audit: IAuditData
  isActive: boolean
}

export interface IBarcodeImportList {
  projectId: string
  barcodeType: string
  barcodeTypeDefinition: IBarcodeTypeDescription
  operationType: string
  status: string
  requestedUser: IRequestUser
  successCount: number
  errorCount: number
  totalCount: number
  projectVersion: string
  id: string
  audit: IBarcodeImportAuditData
  isActive: boolean
}

export interface IBarcodeCreationResult {
  errorMessages: Array<string>
  barcodeId: string
  externalId: string
  startTrackPointKeyOverride: string
  origin: IAddress
  destination: IAddress
  trackingNumber: string
  dimension: IDimension
  customFields: Record<string, IBarcodeCustomField>
  linkedBarcodeIds?: Array<string>
}

export type IBarcodeImport = IBarcodeImportList & {
  barcodeCreationResults: Array<IBarcodeCreationResult>
}

export type IBarcodeImportDetail = {
  projectId: string
  barcodeType: string
  operationType: string
  status: string
  requestedUser?: IUser
  errorMessage?: string
  successCount: number
  errorCount: number
  totalCount: number
  id: string
  audit?: IAuditData
  barcodeCreationResults: Array<IBarcodeCreationResult>
}

export interface IBarcodeProcessList {
  operationType: string
  status: string
  requestedUser: IRequestUser
  successCount: number
  errorCount: number
  totalCount: number
  id: string
  audit: IBarcodeImportAuditData
  isActive: boolean
}

export interface IBarcodeProcessResult {
  errorMessages: Array<string>
  barcodeId: string
  childrenBarcodeIds?: Array<string>
}

export type IBarcodeProcess = IBarcodeProcessList & {
  barcodeProcessResults: Array<IBarcodeProcessResult>
}

export type IBarcodeProcessDetail = {
  operationType: string
  status: string
  requestedUser?: IUser
  errorMessage?: string
  successCount: number
  errorCount: number
  totalCount: number
  id: string
  audit?: IAuditData
  barcodeProcessResults: Array<IBarcodeProcessResult>
}
export interface IBarcodeProcessSearchPayload {
  skip?: number
  count?: number
  t: Function
  te: Function
}

export interface RequestedUser {
  roleType: string
  userType: EUserType
  organizationId: string
  email: string
  firstName: string
  lastName: string
  fullName: string
  id: string
  audit: IAuditData
  isActive: boolean
}

export interface Row {
  projectId: string
  barcodeType: string
  operationType: string
  status: string
  successCount: number
  errorCount: number
  totalCount: number
  barcodeTypeDefinition: IBarcodeTypeDescription
  requestedUser: RequestedUser
  projectName: string
  id: string
  audit: IAuditData
  isActive: boolean
}

export interface IBarcodeImportRow {
  row: Row
  rowIndex: number
  columnIndex: number
}

export interface ILayoutPdf {
  backgroundColor: string
  width: number
  frameBeginX: number
  frameBeginY: number
  marginFrameX: number
  marginFrameY: number
  backgroundFrameWidth: number
  backgroundFrameHeight: number
  marginItemX: number
  marginItemY: number
  headerTextbeginX: number
  headerTextbeginY: number
  headerTextSize: number
  barcodeBeginX: number
  barcodeBeginY: number
  logoWidth: number
  logoHeight: number
  logoBeginY: number
  logoRightX: number
  contentTextSize: number
  contentTextMarginTop: number
  isSupportDiamondFrame?: boolean
  isSupportLogoAndTitle?: boolean
}

export interface ITimeTable {
  projectCode: string
  projectVersion: number
  date: number
  schedules: Record<number, ITimeTableNewSchedule>
  id: string
  audit: IAuditData
  isActive: boolean
}

export interface ITimeTableNewSchedule {
  scheduleCode: string
  barcodeId: string
  paths: Array<ITimeTableNewSchedulePath>
}

export interface ITimeTableNewSchedulePath {
  trackPointOriginKey: number
  trackPointDestinationKey: number
  trackPointOriginName: string
  trackPointDestinationName: string
  scheduledDepartureTime: string
  scheduledArrivalTime: string
  originContainerLocationCode: string
  destinationContainerLocationCode: string
  isDepartureAcked: boolean
  isArrivalAcked: boolean
  routeNumber: number
  operatingCompanyName: string
  vehicleId: number
  departureStatus: string
  arrivalStatus: string
}

export interface ICountryCode {
  code: string
  mask: string[] | null
  dialCode: string
}
export interface IPublishProject {
  projectDraftId?: string
  name: string
  code?: string
  requirePassword?: boolean
  showBarcodeSearchPage: boolean
  showTrackingCountPage: boolean
  allowUnmatchedTracking: boolean
  allowForcePairing: boolean
  isAutoRecycleEndTrackingPoint: boolean
  isRecycleChildren: boolean
  googleAnalyticsMeasurementId: string
  barcodeTypes: Record<string, IBarcodeDefinitionType>
  trackPoints: Record<string, ITrackpoint>
  userGroups?: IProjectUserGroup[]
  locations?: IProjectLocationPayload[]
}

export interface IProjectUserGroup {
  userGroupId?: string
  userIds: string[]
  name: string
  isDefault: boolean
  projectPermission?: IPermission
}

export interface ICarrierUserGroup {
  name: string
  enterpriseId: string
  isDefault?: boolean
  projectPermissions: ITrackingPermissions
  id?: string
  audit?: IAuditData
  userIds: string[]
  isActive?: boolean
  isChecked?: boolean
}

export interface ICarrierProject {
  id: string
  name: string
  code: string
  version: string
}

export interface IUserGroupPermission {
  id: string
  name: string
  code: string
  version: string
  numberTrackPoint: number
}

export interface IUserGroupProject {
  name: string
  code: string
  version: number
}

export interface IUserGroupProjectVersion {
  id: string
  version: number
  name: string
  code: string
}

export interface ITransferData {
  key: string
  label: string
}

export interface IUserGroupMergeData {
  userGroupIds: string[]
  name: string
}

export interface IFieldOption {
  label: string
  value: string
}

export interface IValidation {
  isInvalid: boolean
  exec: Function
}

export interface IImpersonatableUser {
  userId: string
  userName: string
}
export interface IImpersonatable {
  organizationId: string
  organizationName: string
  users: IImpersonatableUser[]
}
export interface ITrackingPointDeletePath {
  fromKey: string
  toKey: string
}

export interface IExportPaginationParams {
  type?: BarcodeExportType
  skip: number
  count: number
}

export interface ICommonExportResponse {
  count: number
  fileSize: number
  encoding: string
  fileFormat: string
  fileUrl: string
  status: string
  audit: IAuditData
  isActive: boolean
  userFullName?: string
}
export interface IBarcodeExportResponse extends ICommonExportResponse {
  operationType: string
  projectCode: string
}

export interface IAccessLogResponse extends ICommonExportResponse {
  targetMonth: string
  enterpriseId: string
}

export interface IMasterDataExportResponse {
  fileName: string
  fileFormat: string
  status: string
  createdDateTime: string
  executeTime: number
  count: number
  fileSize: number
  fileUrl?: string
  userFullName?: string
}

export interface IBarcodeExportHistoryResponse {
  id: number
  fileUrl?: string
  executeTime: number
  count?: number
  type: string
  conditions: string
  requestData: string
  fileFormat: string
  fileSize?: number
  fileName?: string
  encoding?: string
  threadId: number
  status: string
  errorMessage?: string
  barcodeExportHistoryLogs: unknown[]
  isActive: boolean
  createdUserId: string
  createdDateTime: string
  updatedUserId?: string
  updatedDateTime?: string
}

export interface ILocationResponse extends ICommonExportResponse {
  locationId: string
  locationName: string
}

export interface IDownloadWebclipPayload {
  projectCode: string
  projectVersion: number
  icon: string
  label: string
}

export interface IBindingSetting {
  linkageGoodsLineBinding: IRoutePath[]
  startTrackingPointKey: string
}

export type IMappingLabel = IRoutePath
